import { ContentService } from '@blk/cwp-ng-framework';
import { NumberFormatter, NumberToPercentFormatter } from '@ddt-web/data-service';
import { StringUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';

import { FactResult } from '../models/fact-result.model';
import { OutputResult } from '../models/output-result.model';
import { TagResult } from '../models/tag-result.interface';
import { ReplacerRule } from '../rules/replacer-rule';

/**
 * A replacer uses the rules defined in the `replacer`
 * object of the JSON file to convert tags (words preceded by $ and enclosed by #)
 * by evaluating the set of rules to determine the output.
 *
 * `$tags` are resolved by the replacer object defined in the JSON,
 * whereas `#tags#` are resolved against portfolio data itself.
 *
 * If no suitable replacement is found, the tag is returned as is.
 */
export class Replacer {
	public readonly PORT_DATA_TAG_REGEX = /\#[^\%]+?\#/g; // Example: #analyticsMap.something#
	public readonly TAG_REGEX = /\$[A-Za-z_]+[0-9]*\$/g; // Example: $highLowFees$
	public readonly FORMATTER_REGEX = /\<[^]+?\>/g; // Example: <PERCENT>, the whole string would be #analyticsMap.something<PERCENT>#
	private readonly RECURSIVE_LIMIT = 5;
	private readonly LOGGER_PREFIX = `[${Replacer.name}]`;
	public FORMATTER_TYPE = {
		NUMBER: 'NUMBER',
		ABSOLUTE_VALUE: 'ABSOLUTE_VALUE',
		PERCENT: 'PERCENT'
	};

	constructor(public replacerMap: { [key: string]: ReplacerRule }) { }

	public replace(stringToEvaluate: string, portfolioData: any, counter = 1): string | any[] {
		if (counter === this.RECURSIVE_LIMIT) {
			throw new Error('The rules engine has hit its maximum level of replacement checks (' + this.RECURSIVE_LIMIT + '). Please check for recursive tags.');
		}
		if (typeof stringToEvaluate !== 'string') {
			return stringToEvaluate;
		}
		const tagsToEvaluatedMap: Map<string, string | any[]> = new Map();
		stringToEvaluate = this.resolveDataValues(stringToEvaluate, portfolioData, this.PORT_DATA_TAG_REGEX, '#');
		const matches: string[] = stringToEvaluate.match(this.TAG_REGEX);
		if (matches !== null) {
			matches.forEach(tag => {
				const rules = get(this.replacerMap[tag], 'rules');
				if (!rules) {
					console.warn(this.LOGGER_PREFIX + ' No rules found for tag: %s', tag);
					throw Error(this.LOGGER_PREFIX + ' No rules found for tag:' + tag);
				} else {
					let substitutedValues: OutputResult[];
					try {
						substitutedValues = rules.map(rule => rule.evaluate(portfolioData)).filter(x => x);
						if (substitutedValues.some(value => !!value.error)) {
							const errorMsgs = substitutedValues.filter(value => !!value.error).map(errorValue => errorValue.error.debugMessage);
							const message = errorMsgs.join(',');
							throw Error('RULES_ENGINE_ERROR:Replacer error with message:' + message);
						}
						let substitutedValue = this.replacerMap[tag].outputSelector.select(substitutedValues);
						// recursion to resolve tags within replaced values
						substitutedValue = this.replace(substitutedValue, portfolioData, counter + 1);
						tagsToEvaluatedMap.set(tag, substitutedValue);
					} catch (error) {
						throw Error((<Error>error).message);
					}
				}
			});
		}
		tagsToEvaluatedMap.forEach((substitutedValue, tag) => {
			console.log(this.LOGGER_PREFIX + ' replacing \'%s\' with \'%s\' for string \'%s\'', tag, substitutedValue, stringToEvaluate);
			if (substitutedValue instanceof Array) { stringToEvaluate = StringUtil.replaceAll(stringToEvaluate, tag, JSON.stringify(substitutedValue)); }
			else { stringToEvaluate = StringUtil.replaceAll(stringToEvaluate, tag, substitutedValue); }
		});
		return stringToEvaluate;
	}

	public resolveDataValues(stringToEvaluate: string, data: any, regex: RegExp, tagDelimiter: string, counter = 1): string {
		if (counter === this.RECURSIVE_LIMIT) {
			throw new Error('The rules engine has hit its maximum level of replacement checks (' + this.RECURSIVE_LIMIT + '). Please check for recursive tags.');
		}
		const matches: string[] = this.extractMatches(stringToEvaluate, regex);
		if (matches !== null) {
			matches.forEach(tag => {
				const tagWithoutDelimiter = StringUtil.replaceAll(tag, tagDelimiter, '');
				const { formatter, sanitizedTag } = this.processFormatter(tagWithoutDelimiter);
				let dataValue = get(data, sanitizedTag);
				if (typeof dataValue !== 'undefined') {
					if (!(dataValue instanceof FactResult)) { dataValue = new FactResult(dataValue); }
					if (dataValue.error) { throw new Error((<FactResult>dataValue).error.debugMessage); }
					if (typeof dataValue.value === 'string') {
						dataValue.value = this.resolveDataValues(dataValue.value, data, regex, tagDelimiter, counter + 1);
					}
					const formattedValue = this.formatValue(formatter, dataValue.value);
					stringToEvaluate = StringUtil.replaceAll(stringToEvaluate, tag, formattedValue);
				} else {
					throw new Error(sanitizedTag + ':not found');
				}
			});
		}
		return stringToEvaluate;
	}

	public extractMatches(stringToEvaluate: string, regex: RegExp): string[] {
		return stringToEvaluate ? stringToEvaluate.match(regex) : null;
	}

	public extractCleanMatches(stringToEvaluate: string, regex: RegExp, tagDelimiter: string): string[] {
		const matches = this.extractMatches(stringToEvaluate, regex);
		if (matches) {
			return matches.map(tag => StringUtil.replaceAll(tag, tagDelimiter, ''));
		} else {
			return [];
		}
	}

	public populateDCRTags(pageSvg: string, substitutionMap: Object, contentProvider: ContentService): void {
		const matches = this.extractCleanMatches(pageSvg, this.TAG_REGEX, '$');
		// only check contentProvider if no value already exists
		matches.filter(match => match.startsWith('DCR_') && (substitutionMap[match] === null || substitutionMap[match] === undefined))
			.forEach(match => substitutionMap[match] = contentProvider.get(match, match));
	}

	/**
	 * Method to get formatter and sanitized tag which is tag like ate.something
	 * @param tagWithoutDelimiter string like ate.something<PERCENT>
	 *
	 */
	public processFormatter(tagWithoutDelimiter: string): TagResult {
		let formatter;
		let sanitizedTag = tagWithoutDelimiter;
		const formatterString = tagWithoutDelimiter.match(this.FORMATTER_REGEX);
		if (formatterString) {
			formatter = StringUtil.replaceAll(formatterString[0], '<', '');
			formatter = StringUtil.replaceAll(formatter, '>', '');
			sanitizedTag = StringUtil.replaceAll(tagWithoutDelimiter, formatterString[0], '');
		}
		return { formatter, sanitizedTag };
	}

	/**
	 * Method to format value
	 * @param formatter
	 * @param value
	 */
	public formatValue(formatter: string, value: any): string {
		switch (formatter) {
			case this.FORMATTER_TYPE.PERCENT:
				return new NumberToPercentFormatter().format(value);
			case this.FORMATTER_TYPE.NUMBER:
				return new NumberFormatter().format(value);
			case this.FORMATTER_TYPE.ABSOLUTE_VALUE:
				return NumberFormatter.create({ hideMinusNegativeValue: true }).format(value);
			default:
				return value;
		}
	}
}
